import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/utils/utils"
import React from "react"

const ChipVariants = cva(
  "inline-flex w-max cursor-pointer items-center whitespace-nowrap rounded-lg px-6 py-2 transition-colors",
  {
    variants: {
      variant: {
        default: "bg-secondary-10 text-on-secondary-10 hover:bg-hover-secondary-10",
        defaultActive: "bg-on-secondary-10 text-secondary-10",
        secondary: "bg-primary-10 text-on-primary-10",
        info: "pointer-events-none rounded-[5px] bg-primary-05 px-2 py-1 text-[13px] font-normal leading-[19px] tracking-[-0.045em] text-primary-50 xl:rounded-lg xl:px-4 xl:py-2 xl:text-sm lg:text-xxs"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
)

export interface ChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof ChipVariants> {
  asChild?: boolean
  /**
   * Override the rendered element (default `<button>`). Pass `"span"` for a static, non-interactive
   * label so it isn't a focusable button — no tab stop, no focus ring, no misleading "button" role for AT.
   */
  overrideElement?: React.ElementType
}

const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ className, variant, asChild = false, overrideElement, ...props }, ref) => {
    // asChild → Slot (merge onto the child); otherwise the caller-chosen element, defaulting to <button>.
    const Comp = (asChild ? Slot : overrideElement || "button") as React.ElementType
    return <Comp className={cn(ChipVariants({ variant, className }))} ref={ref} {...props} />
  }
)
Chip.displayName = "Chip"

export { Chip, ChipVariants }
